/**
 * Onboarding's one write: `POST /tenants`, then each capacity request against
 * the tenant it returned. The API has no route taking both, so "together" is
 * one mutation the caller fires once, not one HTTP request.
 *
 * Invalidating `/control/me` is not optional. The caller went from no
 * membership to TENANT_ADMIN the moment the tenant exists, and the shell
 * renders its navigation from that query -- left stale, it keeps showing the
 * organisation-less empty state on the screen right after it stopped being
 * true. It fires on creation, not on the mutation's success, because a
 * capacity request failing afterwards does not undo the membership.
 *
 * A retry after a failed capacity request neither creates a second tenant nor
 * re-sends a request the server already holds: `api` keeps at most one OPEN
 * request per kind and would refuse the repeat with a 409.
 *
 * Does not navigate on success: `Capacity.tsx` decides where the flow goes.
 */
import { useRef, useState } from 'react';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import type { ApiFailureError } from '../../../lib/http/errors';
import { useApiClient } from '../../session/client';
import { ME_QUERY_KEY } from '../../session/session';
import { tenantSchema, type Tenant } from '../api/schemas';
import { capacityRequestSchema, type CapacityRequestInput } from '../capacity/api/schemas';

export interface CreateTenantInput {
  name: string;
  requests: CapacityRequestInput[];
}

export function useCreateTenant(): {
  mutation: UseMutationResult<Tenant, ApiFailureError, CreateTenantInput>;
  /** Set once `POST /tenants` succeeded, whatever happened to the requests after it. */
  created: Tenant | null;
} {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [created, setCreated] = useState<Tenant | null>(null);
  const progress = useRef<{ tenant: Tenant | null; sent: Set<CapacityRequestInput['kind']> }>({
    tenant: null,
    sent: new Set(),
  });

  const mutation = useMutation<Tenant, ApiFailureError, CreateTenantInput>({
    mutationFn: async ({ name, requests }) => {
      let tenant = progress.current.tenant;
      if (tenant === null) {
        tenant = await client.postParsed('/tenants', { name }, tenantSchema);
        progress.current.tenant = tenant;
        setCreated(tenant);
        void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      }
      for (const request of requests) {
        if (progress.current.sent.has(request.kind)) continue;
        await client.postParsed(
          `/tenants/${tenant.id}/credit-requests`,
          request,
          capacityRequestSchema,
        );
        progress.current.sent.add(request.kind);
      }
      return tenant;
    },
  });

  return { mutation, created };
}
